import {
  IsBoolean,
  IsDateString,
  IsIn,
  IsOptional,
  IsString,
  MaxLength,
  ValidateIf,
} from 'class-validator';

/**
 * Stage 2 pre-visit contact log. The officer's route to notice (s. 144(2)(a)) or
 * consent (s. 144(6)) is a phone call or a letter, and sufficiency of notice is
 * judged on what was actually attempted — so each attempt is a row, successful
 * or not. A file carries as many as it took.
 *
 * Separate from `service-records.dto`, which is service of the Form 4 at Step
 * 5.3 and starts the 14-day appeal clock. Nothing here starts a clock.
 */

export type PreVisitChannel = 'phone' | 'letter';
export const PRE_VISIT_CHANNELS: PreVisitChannel[] = ['phone', 'letter'];

export type PhoneDirection = 'outbound' | 'inbound';
export const PHONE_DIRECTIONS: PhoneDirection[] = ['outbound', 'inbound'];

/** How a call ended. `refused` is the owner/occupant declining to engage. */
export type PhoneOutcome =
  'spoke' | 'voicemail' | 'no_answer' | 'wrong_number' | 'refused' | 'call_back_requested';
export const PHONE_OUTCOMES: PhoneOutcome[] = [
  'spoke',
  'voicemail',
  'no_answer',
  'wrong_number',
  'refused',
  'call_back_requested',
];

/**
 * `pre_visit` is the letter arranging the first inspection; `courtesy` is the
 * post-inspection Courtesy Letter the spec describes at Step 3, which explains
 * the contravention and sets the date for the next inspection. Both are captured
 * here because both are letters to the same party about the same file, and the
 * officer needs one place to see what was sent.
 */
export type LetterKind = 'pre_visit' | 'courtesy';
export const LETTER_KINDS: LetterKind[] = ['pre_visit', 'courtesy'];

export type LetterDelivery = 'mail' | 'registered_mail' | 'hand' | 'email';
export const LETTER_DELIVERIES: LetterDelivery[] = ['mail', 'registered_mail', 'hand', 'email'];

/** Who was contacted, relative to the property. */
export type ContactedRelationship = 'owner' | 'occupant' | 'agent' | 'other';
export const CONTACTED_RELATIONSHIPS: ContactedRelationship[] = [
  'owner',
  'occupant',
  'agent',
  'other',
];

export interface PreVisitContactDetail {
  id: string;
  fileId: string;
  channel: PreVisitChannel;
  occurredOn: string;
  occurredAtTime: string | null;
  /** Call answered by a person, or letter delivered. */
  successful: boolean;

  partyName: string | null;
  partyRelationship: ContactedRelationship | null;
  partyRelationshipOther: string | null;

  // ── Phone ──────────────────────────────────────────────────────────────────
  phoneNumber: string | null;
  direction: PhoneDirection | null;
  outcome: PhoneOutcome | null;
  callSummary: string | null;

  // ── Letter ─────────────────────────────────────────────────────────────────
  letterKind: LetterKind | null;
  deliveryMethod: LetterDelivery | null;
  mailingAddress: string | null;
  trackingNumber: string | null;
  documentId: string | null;
  responseReceived: boolean;
  responseSummary: string | null;

  officerId: string | null;
  notes: string | null;
  createdAt: string;
}

/**
 * Channel-specific fields are validated conditionally rather than being required
 * outright: a phone row must say how the call ended, a letter row must say how it
 * was delivered, and neither should be able to carry the other's particulars.
 */
export class CreatePreVisitContactDto {
  @IsIn(PRE_VISIT_CHANNELS)
  channel!: PreVisitChannel;

  @IsDateString()
  occurredOn!: string;

  @IsOptional()
  @IsString()
  @MaxLength(16)
  occurredAtTime?: string;

  // `ValidateIf` rather than `IsOptional`, here and on the other two columns
  // backed by NOT NULL storage: `IsOptional` skips validation for null as well
  // as undefined, so an explicit `null` would pass the pipe and then fail at the
  // database as a 500 rather than a 400.
  @ValidateIf((o) => o.successful !== undefined)
  @IsBoolean()
  successful?: boolean;

  @IsOptional()
  @IsString()
  @MaxLength(200)
  partyName?: string;

  @IsOptional()
  @IsIn(CONTACTED_RELATIONSHIPS)
  partyRelationship?: ContactedRelationship;

  // Required only when the relationship is `other` — an unexplained `other`
  // names nobody, and this row is evidence of who the officer reached.
  @ValidateIf((o) => o.partyRelationship === 'other')
  @IsString()
  @MaxLength(200)
  partyRelationshipOther?: string;

  // ── Phone ──────────────────────────────────────────────────────────────────

  @IsOptional()
  @IsString()
  @MaxLength(40)
  phoneNumber?: string;

  @ValidateIf((o) => o.channel === 'phone')
  @IsIn(PHONE_DIRECTIONS)
  direction?: PhoneDirection;

  @ValidateIf((o) => o.channel === 'phone')
  @IsIn(PHONE_OUTCOMES)
  outcome?: PhoneOutcome;

  @IsOptional()
  @IsString()
  callSummary?: string;

  // ── Letter ─────────────────────────────────────────────────────────────────

  @ValidateIf((o) => o.channel === 'letter')
  @IsIn(LETTER_KINDS)
  letterKind?: LetterKind;

  @ValidateIf((o) => o.channel === 'letter')
  @IsIn(LETTER_DELIVERIES)
  deliveryMethod?: LetterDelivery;

  @IsOptional()
  @IsString()
  mailingAddress?: string;

  @IsOptional()
  @IsString()
  @MaxLength(120)
  trackingNumber?: string;

  @IsOptional()
  @IsString()
  documentId?: string;

  @ValidateIf((o) => o.responseReceived !== undefined)
  @IsBoolean()
  responseReceived?: boolean;

  @IsOptional()
  @IsString()
  responseSummary?: string;

  @IsOptional()
  @IsString()
  notes?: string;
}

/**
 * Every field optional — the officer commonly saves a call as "no answer" and
 * fills in the summary after a call back. `channel` is absent deliberately: a
 * row cannot change channel, because its populated columns belong to the one it
 * was created with.
 */
export class UpdatePreVisitContactDto {
  // See `CreatePreVisitContactDto.successful`: these three are backed by NOT
  // NULL columns, so an explicit null has to be rejected by the pipe rather
  // than reaching the database.
  @ValidateIf((o) => o.occurredOn !== undefined)
  @IsDateString()
  occurredOn?: string;

  @IsOptional()
  @IsString()
  @MaxLength(16)
  occurredAtTime?: string;

  @ValidateIf((o) => o.successful !== undefined)
  @IsBoolean()
  successful?: boolean;

  @IsOptional()
  @IsString()
  @MaxLength(200)
  partyName?: string;

  @IsOptional()
  @IsIn(CONTACTED_RELATIONSHIPS)
  partyRelationship?: ContactedRelationship;

  @IsOptional()
  @IsString()
  @MaxLength(200)
  partyRelationshipOther?: string;

  @IsOptional()
  @IsString()
  @MaxLength(40)
  phoneNumber?: string;

  @IsOptional()
  @IsIn(PHONE_DIRECTIONS)
  direction?: PhoneDirection;

  @IsOptional()
  @IsIn(PHONE_OUTCOMES)
  outcome?: PhoneOutcome;

  @IsOptional()
  @IsString()
  callSummary?: string;

  @IsOptional()
  @IsIn(LETTER_KINDS)
  letterKind?: LetterKind;

  @IsOptional()
  @IsIn(LETTER_DELIVERIES)
  deliveryMethod?: LetterDelivery;

  @IsOptional()
  @IsString()
  mailingAddress?: string;

  @IsOptional()
  @IsString()
  @MaxLength(120)
  trackingNumber?: string;

  @IsOptional()
  @IsString()
  documentId?: string;

  @ValidateIf((o) => o.responseReceived !== undefined)
  @IsBoolean()
  responseReceived?: boolean;

  @IsOptional()
  @IsString()
  responseSummary?: string;

  @IsOptional()
  @IsString()
  notes?: string;
}
